<template>
	<div class="product-process-tooltip">
		<div class="tooltip-row">
			<label>Product Process Name: </label>
			{{ data.materialName }}
			<Button
				icon="pi pi-copy"
				@click="copyToClipboard(data.materialName)"
				class="p-button-text p-button-sm copy-button"
				v-tooltip="'Copy material name'"
			/>
		</div>
		<!-- Display emission factors from productProcessProxyEmissionFactor table -->
		<div
			v-if="data.productProcessProxyEmissionFactor && data.productProcessProxyEmissionFactor.length > 0"
			v-for="ef in data.productProcessProxyEmissionFactor"
			:key="ef.id"
			class="tooltip-row"
		>
			<label>{{ getEmissionFactorName(ef.emissionFactorId) }}: </label>
			{{ ef.factor?.toFixed(3) ?? '-' }}
		</div>
	</div>
</template>

<script setup lang="ts">
import { ProductProcessProxy } from "../../entities/NSwagGenerated"
import Button from "primevue/button"
import { useToast } from "primevue/usetoast"
import { useReportsStore } from "@/store/reportsStore"

interface Props {
	data: ProductProcessProxy
}

defineProps<Props>()

const toast = useToast()
const reportsStore = useReportsStore()

// Function to get emission factor name by ID
const getEmissionFactorName = (emissionFactorId: string | undefined): string => {
	if (!emissionFactorId) return "Unknown"
	const emissionFactor = reportsStore.emissionFactors.find((f) => f.id === emissionFactorId)
	return emissionFactor?.emissionFactorName ?? "Unknown"
}

const copyToClipboard = async (text: string | undefined) => {
	if (!text) return

	try {
		await navigator.clipboard.writeText(text)
		toast.add({
			severity: "success",
			summary: "Copied",
			detail: "Material name copied to clipboard",
			life: 2000,
		})
	} catch (err) {
		toast.add({
			severity: "error",
			summary: "Error",
			detail: "Failed to copy to clipboard",
			life: 3000,
		})
	}
}
</script>

<style scoped>
.product-process-tooltip {
	font-size: 14px;
	line-height: 1.5;
}

.tooltip-row {
	display: flex;
	align-items: center;
	margin-bottom: 4px;
	gap: 0.25rem;
}

.tooltip-row:last-child {
	margin-bottom: 0;
}

.copy-button {
	margin-left: 8px;
	padding: 0.25rem;
	height: auto;
	min-height: auto;
	background: transparent !important;
	border: none !important;
	box-shadow: none !important;
	color: #6c757d;
}

.copy-button:hover {
	background: transparent !important;
	color: #495057 !important;
}

.copy-button:focus {
	box-shadow: none !important;
	background: transparent !important;
}

.copy-button .p-button-icon {
	font-size: 0.75rem;
}
</style>
